import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageMeta from '../components/PageMeta';
import Reveal from '../components/Reveal';
import ComparisonTable from '../components/ComparisonTable';
import { FinalCTA, SectionHeading } from '../components/SharedSections';

const { FiArrowRight, FiPhone, FiHeadphones, FiWifi, FiCheck, FiX, FiSearch, FiLayers, FiSettings, FiTrendingUp } = FiIcons;

const solutions = [
  { icon: FiPhone, title: 'UCaaS', text: 'Calling, video, messaging, and mobility in one manageable cloud platform.', to: '/ucaas' },
  { icon: FiHeadphones, title: 'CCaaS', text: 'Smarter routing, channels, supervisor tools, and customer experience.', to: '/ccaas' },
  { icon: FiWifi, title: 'Business Wireless', text: 'Line audits, plan optimization, device strategy, and lower waste.', to: '/wireless' }
];

function HeroGraphic() {
  return (
    <motion.div className="dashboard-mockup" animate={{ y: [0, -8, 0] }} transition={{ duration: 6, repeat: Infinity }}>
      <div className="mockup-head"><span /><span /><span /><strong>Technology fit analysis</strong></div>
      <div className="score-row"><div><small>Current fit</small><strong>54</strong></div><div className="score-arrow">→</div><div className="best-score"><small>Recommended fit</small><strong>92</strong></div></div>
      <div className="chart">
        {[42, 70, 56, 88, 63, 76].map((height, index) => <i key={index} style={{ height: `${height}%` }} />)}
      </div>
      <div className="savings-chip">Potential savings identified <strong>18–27%</strong></div>
    </motion.div>
  );
}

function Home() {
  return (
    <>
      <PageMeta title="Vendor-Neutral Technology Advisory" description="Compare business phone, contact center, and wireless providers without the sales pitch." />
      <section className="home-hero">
        <div className="hero-grid">
          <Reveal>
            <span className="eyebrow light">Independent advice. Better-fit technology.</span>
            <h1>The right business phone, contact center, and wireless plan — without guessing.</h1>
            <p>Cambia Digital helps NYC Tri-State, Brooklyn, and Queens businesses compare leading providers, cut wasted spend, and choose with confidence.</p>
            <div className="hero-actions">
              <a className="button button-white" href="https://meet.cambiadigital.com/consult" target="_blank" rel="noopener noreferrer">Get Your Free Tech Reality Check™</a>
              <Link className="button button-outline-light" to="/compare-providers">Compare Providers</Link>
            </div>
            <div className="hero-proof"><SafeIcon icon={FiCheck} /> No single-provider agenda <SafeIcon icon={FiCheck} /> No-obligation review</div>
          </Reveal>
          <Reveal><HeroGraphic /></Reveal>
        </div>
      </section>

      <section className="trust-strip">
        <p>We evaluate the platforms your team is already researching</p>
        <div className="provider-logos">{['RingCentral', 'ZOOM', 'GoTo', 'Vonage', '8x8', 'Dialpad'].map((name) => <span key={name}>{name}</span>)}</div>
        <div className="stats-row">
          <div><strong>20+</strong><span>Years of experience</span></div>
          <div><strong>25+</strong><span>Providers evaluated</span></div>
          <div><strong>20–30%</strong><span>Typical savings opportunities</span></div>
          <div><strong>1</strong><span>Accountability partner</span></div>
        </div>
      </section>

      <section className="section">
        <Reveal><SectionHeading eyebrow="One advisor, three critical decisions" title="Technology that fits how your business actually works." text="We start with your people, workflows, call patterns, and economics—not a provider quota." center /></Reveal>
        <div className="card-grid three">
          {solutions.map((item) => (
            <Reveal className="solution-card" key={item.title}>
              <div className="icon-tile"><SafeIcon icon={item.icon} /></div>
              <h3>{item.title}</h3><p>{item.text}</p>
              <Link className="text-link" to={item.to}>Explore {item.title} <SafeIcon icon={FiArrowRight} /></Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section tint">
        <div className="split-grid">
          <Reveal>
            <SectionHeading eyebrow="Why vendor-neutral matters" title="Buying direct shows you one answer. We compare the market." text="A provider can explain why its platform works. Cambia determines whether it works for you—and what else deserves a look." />
            <Link className="button button-primary" to="/about">How Cambia Works</Link>
          </Reveal>
          <Reveal className="contrast-card">
            <div className="contrast-column muted"><h3>Buying direct</h3><p><SafeIcon icon={FiX} /> One product lens</p><p><SafeIcon icon={FiX} /> Pricing without market context</p><p><SafeIcon icon={FiX} /> Support may end after setup</p></div>
            <div className="contrast-column preferred"><span className="mini-badge">The Cambia model</span><h3>Independent guidance</h3><p><SafeIcon icon={FiCheck} /> Side-by-side evaluation</p><p><SafeIcon icon={FiCheck} /> ROI and fit modeled clearly</p><p><SafeIcon icon={FiCheck} /> Ongoing account support</p></div>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <Reveal><SectionHeading eyebrow="Research without the rabbit hole" title="See leading UCaaS platforms side by side." text="Core features look similar on paper. The right choice depends on administration, integrations, support, AI, and the details behind the checkmarks." /></Reveal>
        <Reveal><ComparisonTable condensed /></Reveal>
        <div className="center-action"><Link className="button button-secondary" to="/compare-providers">See Full Provider Comparison <SafeIcon icon={FiArrowRight} /></Link></div>
      </section>

      <section className="section dark-section">
        <Reveal><SectionHeading eyebrow="A practical process" title="From unclear bills to a confident decision." center /></Reveal>
        <div className="steps-grid">
          {[
            [FiSearch, '01', 'Free Tech Reality Check™', 'We review costs, tools, pain points, and priorities.'],
            [FiLayers, '02', 'Compare & recommend', 'You get a focused shortlist and a plain-English rationale.'],
            [FiSettings, '03', 'Setup & onboarding', 'We coordinate vendors, porting, training, and rollout.'],
            [FiTrendingUp, '04', 'Support & optimize', 'We stay involved after go-live to protect the outcome.']
          ].map(([icon, number, title, text]) => (
            <Reveal className="step-card" key={number}><span>{number}</span><SafeIcon icon={icon} /><h3>{title}</h3><p>{text}</p></Reveal>
          ))}
        </div>
      </section>

      <section className="section case-section">
        <Reveal className="case-card">
          <span className="eyebrow">Representative outcome</span>
          <blockquote>“The value wasn’t another proposal. It was finally understanding what we were paying for, what we didn’t need, and which platform fit our team.”</blockquote>
          <div><strong>Operations leader</strong><span>Multi-location professional services firm · NYC</span></div>
          <aside><strong>24%</strong><span>Projected annual savings identified</span></aside>
        </Reveal>
      </section>
      <FinalCTA />
    </>
  );
}

export default Home;